import React from 'react'
import {
  WithdrawalAutomationGlobalSettingsDto,
  WithdrawalAutomationGlobalSettingsPayload,
} from '@/api/Wallet/Types/WithdrawalAutomationDto'
import AddButton from '@/components/AddButton/AddButton'
import { DataCard } from '@/components/DataCard/DataCard'
import { FormField, FormFieldLayout, FormSelectOption } from '@/components/Form/FormField'
import { Select } from '@/components/Select'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { FieldArray, Form, FormikProps, withFormik } from 'formik'

import { globalControlsValidationSchema } from './GlobalControlsValidationSchema'

interface OuterProps {
  initialValues: WithdrawalAutomationGlobalSettingsDto
  canEdit: boolean
  currencyOptions: FormSelectOption[]
  onSubmit(values: WithdrawalAutomationGlobalSettingsPayload): Promise<void>
  onToggleMaster(isMasterEnabled: boolean): void
}

const STATUS_OPTIONS = [
  { value: 'enabled', label: 'Enabled' },
  { value: 'disabled', label: 'Disabled' },
]

const GlobalControlsFormUI: React.FC<
  OuterProps & FormikProps<WithdrawalAutomationGlobalSettingsDto>
> = (props) => {
  const { values, setFieldValue, isValid, canEdit, currencyOptions } = props
  const baseCurrency = values.baseCurrencyId

  const handleStatusChange = (value: string | number) => {
    const isMasterEnabled = value === 'enabled'
    setFieldValue('isMasterEnabled', isMasterEnabled)
    props.onToggleMaster(isMasterEnabled)
  }

  return (
    <Form className='flex flex-col gap-4 pt-4'>
      {canEdit && (
        <div className='flex justify-end'>
          <Button type='button' disabled={!isValid} onClick={() => props.handleSubmit()}>
            Save
          </Button>
        </div>
      )}

      <DataCard title='Enable ML Withdrawal Automation'>
        <div className='grid grid-cols-1 gap-6 md:grid-cols-2'>
          <div className='flex flex-col gap-4'>
            <p className='text-sm text-foreground/70'>
              Rule-based automation runs first. When disabled, ML predictions are still recorded but
              not acted upon.
            </p>
            <FormFieldLayout label='Status'>
              <Select
                value={values.isMasterEnabled ? 'enabled' : 'disabled'}
                options={STATUS_OPTIONS}
                readOnly={!canEdit}
                disabled={!canEdit}
                onChange={handleStatusChange}
                className='w-40'
              />
            </FormFieldLayout>
          </div>

          <div className='flex flex-col gap-2'>
            <span className='text-sm font-medium'>Global per-withdrawal max (per currency)</span>
            <FieldArray name='globalCurrencyLimits'>
              {(arrayHelpers) => (
                <div className='flex flex-col gap-2'>
                  <table className='w-full border border-border text-sm'>
                    <thead>
                      <tr className='bg-muted text-left'>
                        <th className='border border-border px-2 py-1 font-medium'>Currency</th>
                        <th className='border border-border px-2 py-1 font-medium'>Max Amount</th>
                        {canEdit && <th className='border border-border px-2 py-1' />}
                      </tr>
                    </thead>
                    <tbody>
                      {values.globalCurrencyLimits.map((limit, index) => (
                        <tr key={index}>
                          <td className='border border-border px-2 py-1'>
                            <Select
                              value={limit.currencyId}
                              options={currencyOptions.map((option) => ({
                                value: option.id,
                                label: option.name,
                              }))}
                              readOnly={!canEdit}
                              disabled={!canEdit}
                              showSearch
                              onChange={(value) =>
                                setFieldValue(
                                  `globalCurrencyLimits.${index}.currencyId`,
                                  String(value)
                                )
                              }
                            />
                          </td>
                          <td className='border border-border px-2 py-1'>
                            <Input
                              type='number'
                              value={limit.maxAmount}
                              readOnly={!canEdit}
                              onChange={(event) =>
                                setFieldValue(
                                  `globalCurrencyLimits.${index}.maxAmount`,
                                  event.target.value
                                )
                              }
                            />
                          </td>
                          {canEdit && (
                            <td className='border border-border px-2 py-1 text-center'>
                              <Button
                                type='button'
                                variant='ghost'
                                onClick={() => arrayHelpers.remove(index)}
                              >
                                ×
                              </Button>
                            </td>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {canEdit && (
                    <AddButton
                      text='Add currency limit'
                      onClick={() => arrayHelpers.push({ currencyId: '', maxAmount: 0 })}
                    />
                  )}
                </div>
              )}
            </FieldArray>
          </div>
        </div>
      </DataCard>

      <DataCard title='Risk Guardrails'>
        <div className='flex flex-col gap-4'>
          <p className='text-sm text-foreground/70'>
            Both platform-level and client-level limits must be satisfied for ML auto-processing to
            occur.
          </p>
          <div className='grid grid-cols-1 gap-6 md:grid-cols-2'>
            <div className='flex flex-col gap-3'>
              <span className='text-sm font-medium'>Platform-level limits (Global exposure)</span>
              <FormField
                name='platformDailyMaxCount'
                label='Max ML automated withdrawals/day'
                type='number'
                readOnly={!canEdit}
              />
              <FormField
                name='platformDailyMaxAmountBase'
                label={`Max ML automated amount/day (${baseCurrency})`}
                type='number'
                readOnly={!canEdit}
              />
            </div>
            <div className='flex flex-col gap-3'>
              <span className='text-sm font-medium'>Client-level limits (Per-client exposure)</span>
              <FormField
                name='clientDailyMaxCount'
                label='Max ML automated withdrawals/client/day'
                type='number'
                readOnly={!canEdit}
              />
              <FormField
                name='clientDailyMaxAmountBase'
                label={`Max ML automated amount/client/day (${baseCurrency})`}
                type='number'
                readOnly={!canEdit}
              />
            </div>
          </div>
        </div>
      </DataCard>
    </Form>
  )
}

export const GlobalControlsForm = withFormik<OuterProps, WithdrawalAutomationGlobalSettingsDto>({
  enableReinitialize: true,
  validateOnMount: true,
  validationSchema: globalControlsValidationSchema,
  mapPropsToValues: (props) => props.initialValues,
  handleSubmit: async (values, { props, setSubmitting }) => {
    try {
      const payload: WithdrawalAutomationGlobalSettingsPayload = {
        isMasterEnabled: values.isMasterEnabled,
        platformDailyMaxCount: Number(values.platformDailyMaxCount),
        platformDailyMaxAmountBase: Number(values.platformDailyMaxAmountBase),
        clientDailyMaxCount: Number(values.clientDailyMaxCount),
        clientDailyMaxAmountBase: Number(values.clientDailyMaxAmountBase),
        globalCurrencyLimits: values.globalCurrencyLimits.map((limit) => ({
          currencyId: limit.currencyId,
          maxAmount: Number(limit.maxAmount),
        })),
      }
      await props.onSubmit(payload)
    } finally {
      setSubmitting(false)
    }
  },
})(GlobalControlsFormUI)
